import React, { useEffect } from 'react';
import { get } from 'lodash';
import { FieldError, useFieldArray, useFormContext } from 'react-hook-form';
import { Flex } from '@chakra-ui/react';
import {
  ButtonIcon,
  ButtonText,
  FormFeedback,
  Input
} from '@morressier/alembic';
import { FieldLabel } from '../Field';
import { FormFieldTextInputArray as IFormFieldTextInputArray } from '@publishing-engine/domain/types/article';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';

interface IFormFieldTextInputArrayProps {
  config: IFormFieldTextInputArray;
  placeholder?: string;
  isEditingAllowed?: boolean;
}

const renderErrorMessage = (
  error: FieldError,
  label: { main: string; subtitle: string },
  validation: { maxLength?: number }
) => {
  let errorMessage = error.message as string;

  if (error.type === 'required' || error.type === 'minLength') {
    errorMessage = `Please enter ${label.main}`;
  }

  if (error.type === 'maxLength') {
    errorMessage = `Maximum length is ${validation.maxLength} characters`;
  }

  return (
    <Flex flexDirection="column" marginTop={2}>
      <FormFeedback variant="error">{errorMessage}</FormFeedback>
    </Flex>
  );
};

const FormFieldTextInputArray: React.FC<IFormFieldTextInputArrayProps> = ({
  config,
  placeholder,
  isEditingAllowed = true
}) => {
  const {
    register,
    formState: { errors },
    watch,
    control,
    clearErrors,
    setError,
    setValue
  } = useFormContext();

  const {
    name,
    params: { validations, labels },
    caseDataFields: { main: field }
  } = config;

  const error = get(errors, field);

  // Create a temporary field name for react-hook-form to work with useFieldArray
  // Currently our schema support stringArray as string[] while use field array supports structure { key: string }[]
  const tempFieldName = `${field}_temp`;

  const { fields, append, remove } = useFieldArray({
    control,
    name: tempFieldName
  });

  const watchedTempArray = watch(tempFieldName) || [];

  // Sync temp object array to actual string[] field expected by AJV
  useEffect(() => {
    const transformed = watchedTempArray
      .map((entry: { text: string }) => entry?.text?.trim())
      .filter(Boolean) as string[];

    setValue(field, transformed, { shouldValidate: true, shouldDirty: true });
  }, [watchedTempArray, field, setValue]);

  // useFieldArray doesn't add first element automatically
  useEffect(() => {
    if (watchedTempArray.length === 0) {
      setValue(tempFieldName, [{ text: '' }]);
    }
  }, []);

  const removeEntry = (index: number) => {
    clearErrors(`${tempFieldName}.${index}.text`);
    remove(index);

    if (fields.length === 1) {
      append({ text: '' });
    }
  };

  const addEntry = () => {
    const hasEmpty = watchedTempArray.some(
      (entry: { text: string }) => !entry.text?.trim()
    );

    if (hasEmpty) {
      const emptyIndex = watchedTempArray.findIndex(
        (entry: { text: string }) => !entry.text?.trim()
      );
      setError(`${tempFieldName}.${emptyIndex}.text`, {
        type: 'required',
        message: `Please enter ${labels.main}`
      });
      return;
    }
    append({ text: '' });
  };

  return (
    <Flex flexDirection="column" alignItems="flex-start" gap={6} width="100%">
      <Flex flexDirection="column" gap={2}>
        <FieldLabel
          isRequired={validations.required}
          label={labels.main}
          subText={labels.subtitle}
          helpText={labels.helpText}
        />
        <Flex
          flexDirection="column"
          width="100%"
          gap={4}
          alignItems="flex-start"
        >
          {fields.map((_, index) => {
            const fieldPath = `${tempFieldName}.${index}.text`;
            const currentValue = watch(fieldPath);
            const fieldError = get(errors, fieldPath);

            return (
              <Flex flexDirection="column" key={index} width="100%">
                <Flex>
                  <Input
                    id={name}
                    value={currentValue ?? ''}
                    {...register(fieldPath, {
                      required: validations.required,
                      maxLength: validations.maxLength
                    })}
                    isInvalid={!!fieldError}
                    placeholder={placeholder || labels.placeholder}
                    isReadOnly={!isEditingAllowed}
                  />
                  <ButtonIcon
                    key={index}
                    ariaLabel={`Delete ${labels.main} `}
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    size="regular"
                    onClick={() => removeEntry(index)}
                  />
                </Flex>
                {fieldError &&
                  renderErrorMessage(
                    fieldError as FieldError,
                    labels,
                    validations
                  )}
              </Flex>
            );
          })}
        </Flex>
        {error && renderErrorMessage(error as FieldError, labels, validations)}
      </Flex>
      <Flex
        flexDirection="column"
        justifyContent="flex-end"
        alignItems="end"
        alignSelf="stretch"
      >
        <ButtonText
          iconLeft={<FontAwesomeIcon icon={faPlus} />}
          onClick={() => addEntry()}
        >
          Add Another
        </ButtonText>
      </Flex>
    </Flex>
  );
};

export default FormFieldTextInputArray;
